import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { InboxViewService } from "@/services/inboxView/InboxViewService";
import type {
  InboxView,
  InboxViewCriteriaInput,
  InboxViewInstantAlert,
  InboxViewReportFormat,
} from "@/types/inboxView/inboxView";

const KEY = ["inbox-views"];

const isActiveStatus = (s?: string | null) =>
  s === "pending" || s === "designing" || s === "extracting";

export const useInboxViews = () => {
  return useQuery({
    queryKey: KEY,
    queryFn: () => InboxViewService.list(),
    staleTime: 30 * 1000,
  });
};

export const useInboxView = (viewId: string | null) => {
  return useQuery({
    queryKey: ["inbox-view", viewId],
    queryFn: () => InboxViewService.get(viewId!),
    enabled: viewId !== null,
    refetchInterval: (query) => {
      const data = query.state.data as { activeRun?: { status?: string } | null } | undefined;
      return isActiveStatus(data?.activeRun?.status) ? 3000 : false;
    },
  });
};

export const useSuggestViews = () => {
  return useMutation({
    mutationFn: (input: { agentFilter?: string[] | null; timeRange?: InboxView["time_range"] }) =>
      InboxViewService.suggest(input.agentFilter, input.timeRange),
  });
};

export const useCreateInboxView = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { prompt: string; agentFilter?: string[] | null; timeRange?: InboxView["time_range"] }) => {
      const designed = await InboxViewService.design(input);
      // Fire extract immediately; UI polls progress via useInboxView.
      const extracted = await InboxViewService.extract(designed.view.id);
      return { view: designed.view, run: extracted.run };
    },
    onSuccess: ({ view }) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["inbox-view", view.id] });
    },
  });
};

export const useRefineInboxView = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ viewId, refinement }: { viewId: string; refinement: string }) =>
      InboxViewService.refine(viewId, refinement),
    onSuccess: (_result, { viewId }) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["inbox-view", viewId] });
    },
  });
};

export const useRerunInboxView = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (viewId: string) => InboxViewService.extract(viewId),
    onSuccess: (_, viewId) => {
      qc.invalidateQueries({ queryKey: ["inbox-view", viewId] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
};

// Starts the one-off upgrade (rules derived, existing rows re-checked) for
// several views at once. Each run continues in the background; a view whose
// run could not be started is reported back in `failed`.
export const useUpgradeInboxViews = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (viewIds: string[]) => {
      const results = await Promise.allSettled(viewIds.map((id) => InboxViewService.extract(id)));
      return { failed: viewIds.filter((_, i) => results[i].status === "rejected") };
    },
    onSuccess: (_result, viewIds) => {
      qc.invalidateQueries({ queryKey: KEY });
      viewIds.forEach((id) => qc.invalidateQueries({ queryKey: ["inbox-view", id] }));
    },
  });
};

export const useDeleteInboxView = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (viewId: string) => InboxViewService.delete(viewId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
};

export const useUpdateInboxView = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      viewId: string;
      name?: string | null;
      pinned?: boolean;
      auto_refresh?: "never" | "daily";
      agent_filter?: string[] | null;
      include_recordings?: boolean;
      report_frequency?: "daily" | "weekly" | "fortnightly" | "monthly" | null;
      report_day_of_week?: number | null;
      report_recipients?: string[];
      report_format?: InboxViewReportFormat;
      instant_alert?: InboxViewInstantAlert | null;
      prompt?: string;
      criteria?: InboxViewCriteriaInput;
    }) => {
      const result = await InboxViewService.update(input);
      // Changing which Genies a view analyzes, or letting meetings in, should
      // surface those conversations right away rather than waiting for a manual refresh.
      if (Object.prototype.hasOwnProperty.call(input, "agent_filter") || input.include_recordings === true) {
        await InboxViewService.extract(input.viewId);
      }
      return result;
    },
    onSuccess: (_result, { viewId }) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["inbox-view", viewId] });
    },
  });
};

export const useSetInboxViewRowHandled = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { viewId: string; rowId: string; handled: boolean }) => InboxViewService.setRowHandled(input),
    onSuccess: (_result, { viewId }) => {
      qc.invalidateQueries({ queryKey: ["inbox-view", viewId] });
    },
  });
};

export const useTestSendViewReport = () => {
  return useMutation({
    mutationFn: (input: {
      viewId: string;
      recipientEmails: string[];
      reportFormat?: InboxViewReportFormat;
      requestId: string;
    }) => InboxViewService.testSendReport(input),
  });
};

export const useExportInboxViewPdf = () => {
  return useMutation({
    mutationFn: (input: { viewId: string; rowIds: string[]; requestId: string }) => InboxViewService.exportPdf(input),
  });
};

export const useRenameInboxViewColumn = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { viewId: string; from: string; to: string; regenerate?: boolean }) =>
      InboxViewService.renameColumn(input),
    onSuccess: (_result, { viewId }) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["inbox-view", viewId] });
    },
  });
};

export const useDeleteInboxViewColumn = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { viewId: string; name: string }) => InboxViewService.deleteColumn(input),
    onSuccess: (_result, { viewId }) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["inbox-view", viewId] });
    },
  });
};
